<template>
  <BaseModal
    v-model="isVisible"
    :title="modalTitle"
    :max-width="1400"
    :show-close-button="true"
    @close="handleClose"
    class="auth-modal"
    content-class="pa-0"
    :z-index="zIndex"
  >
    <!-- Login Form -->
    <LoginForm
      v-if="currentView === 'login'"
      :show-right-panel="true"
      :show-logo-link="false"
      :enable-redirect="false"
      :load-saved-credentials="true"
      :show-query-error="false"
      :use-popup-mode="true"
      @login-success="handleLoginSuccess"
      @login-error="handleLoginError"
      @goto-signup="goToRegister"
      @google-login="handleGoogleLogin"
      @goto-forgot-password="goToForgotPassword"
      :default-redirect-url="computedRedirectUrl"
    />

    <!-- Register Form -->
    <RegisterForm
      v-if="currentView === 'register'"
      :show-left-panel="true"
      :show-logo-link="false"
      :show-language-selector="false"
      :show-recaptcha="true"
      :enable-redirect="false"
      :use-popup-mode="true"
      :redirect-url="computedRedirectUrl"
      @register-success="handleRegisterSuccess"
      @register-error="handleRegisterError"
      @goto-login="goToLogin"
      @google-register="handleGoogleRegister"
      @goto-email-verification="goToEmailVerification"
    />

    <!-- Email Verification Form -->
    <EmailVerificationForm
      v-if="currentView === 'email-verification'"
      :show-left-panel="true"
      :show-logo-link="false"
      :enable-redirect="false"
      :email="registeredEmail"
      :load-email-from-storage="false"
      :success-message="'You can now access all lesson materials!'"
      :show-success-button="false"
      @goto-login="goToLogin"
      @resend-success="handleResendSuccess"
      @resend-error="handleResendError"
      @email-verified="handleEmailVerified"
    />

    <!-- Forgot Password Form -->
    <ForgotPasswordForm
      v-if="currentView === 'forgot-password'"
      use-popup-mode="true"
      :show-right-panel="true"
      :show-logo-link="false"
      :show-language-selector="false"
      :show-background-images="false"
      :show-recaptcha="true"
      :enable-redirect="false"
      @forgot-password-success="handleForgotPasswordSuccess"
      @forgot-password-error="handleForgotPasswordError"
      @goto-login="goToLogin"
      @goto-signup="goToRegister"
      @email-sent="handleEmailSent"
    />
  </BaseModal>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  defineProps,
  defineEmits,
  defineExpose,
  inject,
} from "vue";
import EmailVerificationForm from "@/components/EmailVerificationForm.vue";
import ForgotPasswordForm from "@/components/ForgotPasswordForm.vue";
import LoginForm from "@/components/LoginForm.vue";
import BaseModal from "@/components/modal/BaseModal.vue";
import RegisterForm from "@/components/RegisterForm.vue";
import makercartActivityService from "@/services/makercartActivityService";
import { useUserState } from "@/composeables/useUserState";
import { useRoute } from "vue-router";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  // Controls which view to show initially
  initialView: {
    type: String,
    default: "login", // 'login', 'register', 'email-verification', 'forgot-password'
    validator: (value) =>
      ["login", "register", "email-verification", "forgot-password"].includes(
        value
      ),
  },
  zIndex: {
    type: Number,
    default: 240,
  },
  redirectUrl: {
    type: String,
    default: "",
  },
});

const notify = inject("notify");

const { updateUserState } = useUserState();
const route = useRoute();

const emit = defineEmits([
  "close",
  "login-success",
  "register-success",
  "email-verified",
  "password-reset-sent",
]);

// State management
const currentView = ref(props.initialView);
const registeredEmail = ref("");
const computedRedirectUrl = ref("");

const isVisible = computed({
  get: () => props.isOpen,
  set: (value) => {
    if (!value) {
      emit("close");
    }
  },
});

// Computed modal title based on current view
const modalTitle = computed(() => {
  switch (currentView.value) {
    case "login":
      return "Sign In/Sign Up for free to view all content";
    case "register":
      return "Create your account";
    case "email-verification":
      return "Verify your email";
    case "forgot-password":
      return "Reset your password";
    default:
      return "Authentication";
  }
});

// Watch for initial view changes
watch(
  () => props.initialView,
  (newView) => {
    currentView.value = newView;
  }
);

// Function to build redirect URL from current route
function buildRedirectUrl() {
  if (props.redirectUrl) {
    return props.redirectUrl;
  }

  // Build URL from route if no explicit redirectUrl prop
  const baseUrl = window.location.origin + route.path;
  const queryParams = new URLSearchParams(route.query).toString();
  return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
}

// Reset to login view when modal is opened
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      currentView.value = props.initialView;
      registeredEmail.value = "";

      // Set redirect URL when modal opens (captures current route state)
      computedRedirectUrl.value = buildRedirectUrl();

      // Record the prompt when modal is opened
      try {
        await makercartActivityService.recordPrompt();
        console.log("Prompt recorded successfully");
      } catch (error) {
        console.error("Failed to record prompt:", error);
      }
    }
  }
);

// Navigation methods
function goToLogin() {
  currentView.value = "login";
}

function goToRegister() {
  currentView.value = "register";
}

function goToEmailVerification(email = "") {
  if (email) {
    registeredEmail.value = email;
  }
  currentView.value = "email-verification";
}

function goToForgotPassword() {
  currentView.value = "forgot-password";
}

const handleClose = () => {
  emit("close");
};

function handleLoginSuccess(data) {
  notify.plain("You’re successfully logged in!");
  emit("login-success", data);
  handleClose();
}

function handleLoginError(error) {
  console.log("Modal login error:", error);
}

function handleGoogleLogin() {
  console.log("Google login initiated from modal");
}

function handleRegisterSuccess(data) {
  console.log("Modal register successful:", data);
  emit("register-success", data);
  goToEmailVerification(data.email);
}

function handleRegisterError(error) {
  console.log("Modal register error:", error);
}

function handleGoogleRegister() {
  console.log("Google register initiated from modal");
}

function handleResendSuccess(email) {
  console.log("Resend verification successful for:", email);
}

function handleResendError(error) {
  console.log("Resend verification error:", error);
}

async function handleEmailVerified(data) {
  await updateUserState();
  notify.plain("Your email has been verified successfully!");
  emit("email-verified", data);
}

function handleForgotPasswordSuccess(email) {
  console.log("Forgot password email sent to:", email);
  emit("password-reset-sent", email);
}

function handleForgotPasswordError(error) {
  console.log("Forgot password error:", error);
}

function handleEmailSent(email) {
  console.log("Password reset email sent to:", email);
}

defineExpose({
  goToLogin,
  goToRegister,
  goToEmailVerification,
  goToForgotPassword,
  currentView: computed(() => currentView.value),
});
</script>

<style scoped>
.auth-modal :deep(.v-card) {
  border-radius: 16px;
}

/* Override form styles for modal */
.auth-modal :deep(.login-form),
.auth-modal :deep(.register-form),
.auth-modal :deep(.email-verification-form),
.auth-modal :deep(.forgot-password-form) {
  height: auto;
  min-height: auto;
}

.auth-modal :deep(div[style*="height: 100vh"]) {
  height: auto !important;
  min-height: auto !important;
}

.auth-modal :deep(div[style*="width: 50%"]:only-child) {
  width: 100% !important;
}

.auth-modal :deep(img[style*="position: absolute"]) {
  display: none;
}

.auth-modal :deep(.v-card-text) {
  padding: 0;
}

.auth-modal :deep(router-link) {
  display: none;
}

.auth-modal :deep(.v-dialog) {
  align-items: center;
}

@media (max-width: 768px) {
  .auth-modal :deep(.v-card) {
    margin: 12px;
    max-height: calc(100vh - 24px);
  }

  .auth-modal :deep(div[style*="width: 50%"]) {
    width: 100% !important;
  }
}
</style>
